import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  Briefcase,
  Sparkles,
  Stethoscope,
  UtensilsCrossed,
  HardHat,
  Camera,
  Scale,
  MessageSquare,
  Palette,
  PenTool,
  Rocket,
  Wallet,
  RefreshCw,
  Package,
  LogOut,
  Key,
  Quote,
} from 'lucide-react';
import SEO from '../components/SEO';
import FAQSection from '../components/sections/FAQSection';
import CTASection from '../components/sections/CTASection';
import { useInView } from '../hooks/useInView';
import {
  audienceSegments,
  includedChecklist,
  processStepsNew,
  abonamentReasons,
  portfolioItems,
  testimonials,
  homeFAQ,
  companyInfo,
} from '../data/content';
import { pricingPlans } from '../data/mockData';

const iconMap = {
  Briefcase,
  Sparkles,
  Stethoscope,
  UtensilsCrossed,
  HardHat,
  Camera,
  Scale,
  MessageSquare,
  Palette,
  PenTool,
  Rocket,
  Wallet,
  RefreshCw,
  Package,
  LogOut,
  Key,
};

function HeroSection() {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative h-screen min-h-[640px] flex items-center justify-center overflow-hidden bg-gradient-to-r from-[#2d4059] to-[#6b9bc3]">
      <div className="absolute inset-0 bg-black/10" />
      <div className="relative z-10 max-w-[120rem] mx-auto px-6 text-center">
        <h1
          className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-heading font-extrabold text-white mb-6 leading-tight transition-all duration-700 ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          Profesjonalna strona internetowa
          <br />
          <span className="text-[#f1d58f]">bez opłaty startowej</span>
        </h1>
        <p
          className={`text-lg sm:text-xl md:text-2xl text-white/90 mb-10 max-w-3xl mx-auto font-paragraph transition-all duration-700 delay-200 ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          Projekt, wdrożenie, hosting, SSL i opieka techniczna w miesięcznym abonamencie. Start w 7 dni.
        </p>
        <div
          className={`flex flex-col sm:flex-row gap-4 justify-center items-center transition-all duration-700 delay-500 ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
          }`}
        >
          <Link
            to="/cennik"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#e74c3c] hover:bg-[#d44332] text-white text-lg font-semibold rounded-md shadow-lg hover:shadow-xl transition-all"
          >
            Zobacz cennik
            <ArrowRight className="ml-1 h-5 w-5" />
          </Link>
          <Link
            to="/kontakt"
            className="inline-flex items-center px-8 py-4 border border-white text-white hover:bg-white hover:text-[#2d4059] text-lg rounded-md transition-all"
          >
            Umów bezpłatną rozmowę
          </Link>
        </div>
      </div>
    </section>
  );
}

function AudienceSection() {
  const [ref, isInView] = useInView();

  return (
    <section className="py-24 bg-white">
      <div className="max-w-[100rem] mx-auto px-6">
        <div ref={ref} className="text-center mb-16">
          <h2
            className={`text-3xl sm:text-4xl md:text-5xl font-heading font-extrabold text-[#2c3e50] mb-4 transition-all duration-700 ${
              isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            Dla kogo tworzymy strony
          </h2>
          <p className="text-lg text-[#2c3e50]/70 max-w-2xl mx-auto font-paragraph">
            Robimy strony, które rozumieją swoją branżę. Kliknij, żeby zobaczyć, co przygotujemy dla Ciebie.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {audienceSegments.map((item, i) => {
            const Icon = iconMap[item.icon];
            return (
              <Link
                key={item.slug}
                to={item.link}
                className="group bg-[#edf1f5] hover:bg-white hover:shadow-xl rounded-xl p-6 transition-all border border-transparent hover:border-[#2c3e50]/10"
              >
                <div className="flex items-start gap-4">
                  {Icon && (
                    <div className="shrink-0 w-12 h-12 rounded-lg bg-white group-hover:bg-[#edf1f5] flex items-center justify-center transition-colors">
                      <Icon className="h-6 w-6 text-[#2c3e50]" strokeWidth={1.8} />
                    </div>
                  )}
                  <div>
                    <h3 className="font-heading font-bold text-[#2c3e50] text-lg mb-2 group-hover:text-[#e74c3c] transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-sm text-[#2c3e50]/70 font-paragraph leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ChecklistSection() {
  return (
    <section className="py-24 bg-[#edf1f5]">
      <div className="max-w-[100rem] mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading font-extrabold text-[#2c3e50] mb-6">
              Co dokładnie dostajesz
            </h2>
            <p className="text-lg text-[#2c3e50]/70 font-paragraph mb-6">
              Żadnych marketingowych ogólników. Oto konkretna lista tego, co wchodzi w cenę każdego pakietu.
            </p>
            <p className="text-base text-[#2c3e50]/60 font-paragraph">
              Masz pytanie, czy coś konkretnego jest w cenie?{' '}
              <Link to="/kontakt" className="text-[#e74c3c] font-semibold hover:underline">
                Napisz do nas
              </Link>
              .
            </p>
          </div>
          <ul className="space-y-3">
            {includedChecklist.map((item) => (
              <li key={item} className="flex items-start gap-3 bg-white rounded-lg p-4 shadow-sm">
                <CheckCircle2 className="shrink-0 text-teal-500 mt-0.5" size={20} />
                <span className="text-[#2c3e50]/80 font-paragraph">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function ProcessSection() {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-[100rem] mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading font-extrabold text-[#2c3e50] mb-4">
            Jak to działa
          </h2>
          <p className="text-lg text-[#2c3e50]/70 max-w-2xl mx-auto font-paragraph">
            Cztery kroki, jedna prosta współpraca. Bez żargonu, bez niespodzianek.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {processStepsNew.map((step) => {
            const Icon = iconMap[step.icon];
            return (
              <div
                key={step.number}
                className="bg-[#edf1f5] rounded-xl p-6 relative overflow-hidden"
              >
                <div className="absolute top-4 right-4 text-5xl font-heading font-extrabold text-[#2c3e50]/10">
                  {step.number}
                </div>
                {Icon && <Icon className="h-10 w-10 text-[#2c3e50] mb-4" strokeWidth={1.6} />}
                <h3 className="text-xl font-heading font-bold text-[#2c3e50] mb-2">{step.title}</h3>
                <p className="text-sm text-[#2c3e50]/70 font-paragraph mb-3">{step.short}</p>
                <p className="text-sm text-[#2c3e50]/60 font-paragraph leading-relaxed">
                  {step.description}
                </p>
              </div>
            );
          })}
        </div>
        <div className="text-center mt-10">
          <Link
            to="/jak-to-dziala"
            className="inline-flex items-center gap-2 text-[#e74c3c] font-paragraph font-semibold hover:underline"
          >
            Pełny opis procesu współpracy
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function AbonamentSection() {
  return (
    <section className="py-24 bg-[#edf1f5]">
      <div className="max-w-[100rem] mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading font-extrabold text-[#2c3e50] mb-4">
            Dlaczego abonament, a nie jednorazowa opłata
          </h2>
          <p className="text-lg text-[#2c3e50]/70 max-w-2xl mx-auto font-paragraph">
            Pięć powodów, dla których klienci wybierają model abonamentowy.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {abonamentReasons.map((reason) => {
            const Icon = iconMap[reason.icon];
            return (
              <div key={reason.title} className="bg-white rounded-xl p-6 shadow-sm">
                {Icon && <Icon className="h-10 w-10 text-[#e74c3c] mb-4" strokeWidth={1.6} />}
                <h3 className="text-lg font-heading font-bold text-[#2c3e50] mb-2">
                  {reason.title}
                </h3>
                <p className="text-sm text-[#2c3e50]/70 font-paragraph leading-relaxed">
                  {reason.description}
                </p>
              </div>
            );
          })}
        </div>
        <div className="text-center mt-10">
          <Link
            to="/strony-internetowe-w-abonamencie"
            className="inline-flex items-center gap-2 text-[#e74c3c] font-paragraph font-semibold hover:underline"
          >
            Więcej o modelu abonamentowym
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function PortfolioPreviewSection() {
  const items = portfolioItems.slice(0, 6);
  return (
    <section className="py-24 bg-white">
      <div className="max-w-[100rem] mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading font-extrabold text-[#2c3e50] mb-4">
            Przykładowe realizacje
          </h2>
          <p className="text-lg text-[#2c3e50]/70 max-w-2xl mx-auto font-paragraph">
            Kilka przykładów stron, które moglibyśmy przygotować dla firm z różnych branż.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((item) => (
            <div
              key={item.slug}
              className="group bg-[#edf1f5] rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all"
            >
              <div className="aspect-video overflow-hidden bg-[#2c3e50]/10">
                <img
                  src={item.image}
                  alt={item.imageAlt}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs uppercase tracking-wider text-[#e74c3c] font-paragraph font-semibold">
                    {item.industry}
                  </span>
                  {item.placeholder && (
                    <span className="text-[10px] uppercase text-[#2c3e50]/40 font-paragraph">
                      {/* TODO: uzupełnić prawdziwymi danymi */}
                      Przykład
                    </span>
                  )}
                </div>
                <h3 className="font-heading font-bold text-[#2c3e50] text-lg mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-[#2c3e50]/70 font-paragraph leading-relaxed">
                  {item.description}
                </p>
              </div>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link
            to="/portfolio"
            className="inline-flex items-center gap-2 text-[#e74c3c] font-paragraph font-semibold hover:underline"
          >
            Zobacz całe portfolio
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function TestimonialsPreviewSection() {
  const items = testimonials.slice(0, 3);
  return (
    <section className="py-24 bg-[#edf1f5]">
      <div className="max-w-[100rem] mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading font-extrabold text-[#2c3e50] mb-4">
            Opinie klientów
          </h2>
          <p className="text-lg text-[#2c3e50]/70 max-w-2xl mx-auto font-paragraph">
            {/* TODO: uzupełnić prawdziwymi danymi */}
            Przykładowe opinie, jakich spodziewamy się po współpracy.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((t, i) => (
            <div key={i} className="bg-white rounded-xl p-6 shadow-sm relative">
              <Quote className="h-8 w-8 text-[#e74c3c]/30 mb-3" />
              <p className="text-[#2c3e50]/80 font-paragraph leading-relaxed mb-4">„{t.quote}”</p>
              <div className="border-t border-[#2c3e50]/10 pt-3">
                <p className="font-paragraph font-semibold text-[#2c3e50]">{t.name}</p>
                <p className="text-xs text-[#2c3e50]/60 font-paragraph">
                  {t.company} · {t.industry}
                </p>
              </div>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link
            to="/opinie"
            className="inline-flex items-center gap-2 text-[#e74c3c] font-paragraph font-semibold hover:underline"
          >
            Wszystkie opinie
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function PricingPreviewSection() {
  const plans = pricingPlans.slice(0, 3);
  return (
    <section className="py-24 bg-white">
      <div className="max-w-[100rem] mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading font-extrabold text-[#2c3e50] mb-4">
            Cennik w skrócie
          </h2>
          <p className="text-lg text-[#2c3e50]/70 max-w-2xl mx-auto font-paragraph">
            Trzy pakiety dopasowane do wielkości firmy. Bez opłaty startowej.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`rounded-xl p-6 ${
                plan.popular
                  ? 'bg-[#2c3e50] text-white shadow-xl'
                  : 'bg-[#edf1f5] text-[#2c3e50]'
              }`}
            >
              <h3 className="font-heading font-bold text-xl mb-2">{plan.name}</h3>
              <div className="flex items-baseline flex-wrap gap-2 mb-4">
                {plan.oldPrice && (
                  <span className={`font-heading font-bold text-xl line-through ${plan.popular ? 'text-white/50' : 'text-[#2c3e50]/40'}`}>
                    {plan.oldPrice} zł
                  </span>
                )}
                <span className={`font-heading font-extrabold text-4xl ${plan.oldPrice ? 'text-[#e74c3c]' : ''}`}>{plan.price} zł</span>
                <span className="opacity-70 text-sm">/mies.</span>
              </div>
              <p
                className={`text-sm font-paragraph mb-4 ${
                  plan.popular ? 'text-white/80' : 'text-[#2c3e50]/70'
                }`}
              >
                {plan.description}
              </p>
              <Link
                to="/cennik"
                className={`block text-center w-full py-2.5 rounded-md font-semibold text-sm transition-colors ${
                  plan.popular
                    ? 'bg-[#e74c3c] hover:bg-[#d44332] text-white'
                    : 'bg-[#2c3e50] hover:bg-[#34495e] text-white'
                }`}
              >
                Szczegóły pakietu
              </Link>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link
            to="/cennik"
            className="inline-flex items-center gap-2 text-[#e74c3c] font-paragraph font-semibold hover:underline"
          >
            Pełny cennik
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

const localBusinessJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ProfessionalService',
  name: companyInfo.name,
  legalName: companyInfo.legalName,
  url: 'https://www.webnatak.pl',
  description:
    'Profesjonalne tworzenie stron internetowych w modelu abonamentowym - bez opłaty startowej, z hostingiem i opieką techniczną.',
  telephone: companyInfo.phoneDigits,
  email: companyInfo.email,
  taxID: companyInfo.nip,
  vatID: `PL${companyInfo.nip}`,
  address: {
    '@type': 'PostalAddress',
    streetAddress: companyInfo.street,
    addressLocality: companyInfo.city,
    postalCode: companyInfo.postalCode,
    addressCountry: companyInfo.countryCode,
  },
  areaServed: [
    { '@type': 'City', name: 'Świdnica' },
    { '@type': 'City', name: 'Wałbrzych' },
    { '@type': 'City', name: 'Dzierżoniów' },
    { '@type': 'State', name: 'Dolnośląskie' },
    { '@type': 'Country', name: 'Polska' },
  ],
  serviceType: [
    'Tworzenie stron internetowych',
    'Opieka nad stronami',
    'Strony w abonamencie',
    'Projektowanie stron WWW',
  ],
};

export default function HomePage() {
  return (
    <>
      <SEO
        title="Strony internetowe w abonamencie bez opłaty startowej"
        description="WebNaTak - profesjonalne strony internetowe w miesięcznym abonamencie. Projekt, hosting, SSL i opieka techniczna w jednej cenie. Start w 7 dni, bez opłaty startowej."
        canonical="/"
        jsonLd={localBusinessJsonLd}
      />
      <div className="min-h-screen bg-[#edf1f5]">
        <HeroSection />
        <AudienceSection />
        <ChecklistSection />
        <ProcessSection />
        <AbonamentSection />
        <PortfolioPreviewSection />
        <TestimonialsPreviewSection />
        <PricingPreviewSection />
        <FAQSection
          title="Najczęściej zadawane pytania"
          subtitle="Najkrótsze odpowiedzi na pytania, które padają najczęściej. Pełna lista na stronie FAQ."
          items={homeFAQ}
          background="bg-[#edf1f5]"
        />
        <CTASection
          title="Porozmawiajmy o Twojej stronie"
          description="15 minut rozmowy, żebyśmy mogli zrozumieć, czego potrzebujesz. Bez zobowiązań."
          primaryLabel="Umów bezpłatną rozmowę"
          primaryTo="/kontakt"
          secondaryLabel="Zobacz cennik"
          secondaryTo="/cennik"
        />
      </div>
    </>
  );
}
